import styled from "styled-components";
import {
  colors,
  getTypographyFontStyle,
  device,
} from "../../styles/global.style";

const MainContainer = styled.div`
  overflow: hidden;
`;

const Description = styled.p`
  ${getTypographyFontStyle({ variant: "body1", weight: 400 })};
  color: ${(props) => props.color || colors.dark};
  margin-top: 20px;
  line-height: 1.7;
`;

const IntroContainer = styled.div`
  padding: 16px;
  margin-top: 80px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media ${device.md} {
    padding: 48px 16px;
    margin-top: 100px;
    grid-template-columns: 1fr 2fr;
    gap: 32px;
  }
`;

const ServiceContainer = styled.div`
  position: relative;
  padding: 24px 16px;
  padding-top: 48px;
  padding-bottom: 0;

  @media ${device.md} {
    padding: 40px;
    padding-top: 60px;
    padding-bottom: 0;
  }
`;

const SecurityImg = styled.img`
  width: 160px;

  @media ${device.md} {
    width: 280px;
  }

  @media ${device.lg} {
    width: 320px;
  }
`;

const HappyCustomers = styled.h1`
  font-size: 48px;
  color: ${colors.primary};
  font-family: "Poppins", sans-serif;
  font-weight: 700;

  @media ${device.md} {
    font-size: 64px;
  }

  @media ${device.lg} {
    font-size: 80px;
  }
`;

const CustomerGrowth = styled.div`
  background-color: ${colors.white};
  padding: 20px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const GrowthWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  max-width: 300px;

  @media ${device.md} {
    margin-top: 0;
    max-width: 340px;
  }
`;

const GrowthSubtitle = styled.p`
  ${getTypographyFontStyle({ variant: "body2", weight: 400 })};
  color: ${colors.lightdark};
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;

  @media ${device.md} {
    width: 28px;
    height: 28px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-right: 12px;
  background-color: ${colors.primary};
  flex-shrink: 0;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  width: 44px;
  height: 44px;
  background: ${colors.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-50%) scale(1.05);
  }

  ${(props) => props.left && "left: -22px;"}
  ${(props) => props.right && "right: -22px;"}

  @media (max-width: 768px) {
    display: none !important;
  }
`;

const Industries = styled.div`
  margin-top: 24px;
`;

// New Solar-Focused Styled Components

const BenefitsSection = styled.section`
  padding: 60px 0;
  background: ${colors.light};
`;

const BenefitCard = styled.div`
  background: ${colors.white};
  padding: 32px 24px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(10, 22, 40, 0.06);
  transition: all 0.3s ease;
  height: 100%;
  text-align: center;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(10, 22, 40, 0.12);
  }
`;

const BenefitIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const BenefitTitle = styled.h3`
  font-family: "Space Grotesk", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.secondary};
  margin-bottom: 12px;
`;

const BenefitDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 15px;
  color: ${colors.darkGrey};
  line-height: 1.6;
  margin: 0;
`;

const HowItWorksSection = styled.section`
  padding: 60px 0;
  background: linear-gradient(180deg, ${colors.white} 0%, ${colors.light} 100%);
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px;
  position: relative;
`;

const StepNumber = styled.div`
  font-family: "Space Grotesk", sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: ${colors.primary};
  opacity: 0.3;
  line-height: 1;
  margin-bottom: 16px;
`;

const StepContent = styled.div``;

const StepTitle = styled.h4`
  font-family: "Space Grotesk", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.secondary};
  margin-bottom: 12px;
`;

const StepDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.darkGrey};
  line-height: 1.6;
  margin: 0;
`;

const CTASection = styled.section`
  padding: 80px 0;
  background: ${colors.light};
`;

const SolarCalculator = styled.div`
  background: ${colors.white};
  border-radius: 24px;
  padding: 48px 32px;
  box-shadow: 0 8px 40px rgba(10, 22, 40, 0.08);

  .calculator-header {
    text-align: center;
    margin-bottom: 40px;
  }

  .calculator-body {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
    align-items: center;

    @media ${device.md} {
      grid-template-columns: 1fr 1fr;
    }
  }

  .input-section {
    label {
      display: block;
      font-family: "Space Grotesk", sans-serif;
      font-weight: 600;
      color: ${colors.secondary};
      margin-bottom: 16px;
      font-size: 16px;
    }

    .bill-display {
      font-family: "Space Grotesk", sans-serif;
      font-size: 32px;
      font-weight: 700;
      color: ${colors.primary};
      margin-top: 16px;
    }
  }
`;

const CalculatorInput = styled.input`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: ${colors.tertiary};
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${colors.primary};
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(245, 166, 35, 0.4);
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${colors.primary};
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 8px rgba(245, 166, 35, 0.4);
  }
`;

const SavingsDisplay = styled.div`
  background: linear-gradient(135deg, ${colors.secondary} 0%, ${colors.navy} 100%);
  border-radius: 16px;
  padding: 32px;
  text-align: center;

  .savings-item {
    margin-bottom: 24px;
  }

  .savings-label {
    display: block;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    color: ${colors.light};
    opacity: 0.8;
    margin-bottom: 8px;
  }

  .savings-amount {
    display: block;
    font-family: "Space Grotesk", sans-serif;
    font-size: 36px;
    font-weight: 700;
    color: ${colors.primary};

    @media ${device.md} {
      font-size: 42px;
    }
  }
`;

// Portfolio Gallery Section
const PortfolioSection = styled.section`
  padding: 60px 0;
  background: ${colors.white};
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media ${device.md} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${device.lg} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PortfolioItem = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 4/3;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(10, 22, 40, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(10, 22, 40, 0.15);

    .portfolio-overlay {
      opacity: 1;
    }
  }
`;

const PortfolioImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${PortfolioItem}:hover & {
    transform: scale(1.05);
  }
`;

const PortfolioOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(10, 22, 40, 0.9) 0%, transparent 100%);
  padding: 24px;
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const PortfolioTitle = styled.h4`
  font-family: "Space Grotesk", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.white};
  margin: 0 0 4px 0;
`;

const PortfolioLocation = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.primary};
  opacity: 0.9;
`;

const ViewAllLink = styled.button`
  font-family: "Space Grotesk", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.primary};
  background: none;
  border: none;
  cursor: pointer;
  margin-top: 16px;
  padding: 8px 16px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: ${colors.primaryDark};
    transform: translateX(4px);
  }
`;

export {
  SecurityImg,
  MainContainer,
  Description,
  IntroContainer,
  ServiceContainer,
  HappyCustomers,
  CustomerGrowth,
  GrowthWrapper,
  GrowthSubtitle,
  Industries,
  Icon,
  IconWrapper,
  ArrowButton,
  BenefitsSection,
  BenefitCard,
  BenefitIcon,
  BenefitTitle,
  BenefitDescription,
  HowItWorksSection,
  StepContainer,
  StepNumber,
  StepContent,
  StepTitle,
  StepDescription,
  CTASection,
  SolarCalculator,
  CalculatorInput,
  SavingsDisplay,
  PortfolioSection,
  PortfolioGrid,
  PortfolioItem,
  PortfolioImage,
  PortfolioOverlay,
  PortfolioTitle,
  PortfolioLocation,
  ViewAllLink,
};
